'use client';

import { FOUNDERS_REFUND_POLICY } from '@shared/all/domains/products';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import { formatUSD } from '@shared/utils/helpers/formatCurrency';
import formatDate from '@shared/utils/helpers/formatDate';
import useProducts from '@shared/utils/hooks/useProducts';
import useRole from '@shared/utils/hooks/useRole';
import useUser from '@shared/utils/hooks/useUser';
import { Check } from 'lucide-react';
import { useContext, useState } from 'react';

import { client } from '../../utils/trpc';
import Button from '../common/Button';
import PurchaseModal from '../common/PurchaseModal';

export const FoundersToken = () => {
  const { openAuthContinueModal } = useContext(AuthWidgetContext);
  const { products } = useProducts(client);
  const { currentUser } = useUser();
  const { premiumExpires } = useRole(client);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  const purchaseHandler = () => {
    if (!currentUser.isAuthenticated) {
      openAuthContinueModal();
      return;
    }
    setButtonLoading(true);
    setPurchaseModalOpen(true);
    setButtonLoading(false);
  };

  return (
    <>
      {products
        .filter((product) => product.type === 'FOUNDERS_TOKEN')
        .slice(0, 1)
        .map((product) => {
          const remaining = product.inventory;
          const totalTrons =
            (product.tron?.quantity ?? 0) + (product.bonusTrons ?? 0);
          const buttonText = product.owned
            ? `You're a Founding Member${
                premiumExpires
                  ? ` — Premium until ${formatDate(premiumExpires)}`
                  : ''
              }`
            : product.statusLabel === 'Sold Out'
              ? 'Sold Out'
              : `Purchase for ${formatUSD(product.displayPrice)}`;

          // Wave ladder is 50% → 25% → 0% off the same list price
          let waveNote = 'Final wave — once sold out, gone forever.';
          if (product.discount >= 50) {
            waveNote = `Price rises to ${formatUSD(product.price * 0.75)} (25% off) when this wave sells out, then ${formatUSD(product.price)} for the final wave. 10,000 total, ever.`;
          } else if (product.discount > 0) {
            waveNote = `Price rises to ${formatUSD(product.price)} when this wave sells out — the final wave. 10,000 total, ever.`;
          }

          const features = [
            'Lifetime Factiii Premium (100-year subscription)',
            `${totalTrons.toLocaleString()} trons${
              product.bonusTrons > 0
                ? ` (includes ${product.bonusTrons.toLocaleString()} bonus)`
                : ''
            }`,
            'Exclusive Founders space access',
            'Spend every tron at exact AI cost, forever'
          ];

          return (
            <div
              key={product.id}
              data-testid="founders-token-card"
              className="anim-enter flex flex-col gap-4 rounded-xl border-2 border-primary bg-primary/5 p-4 [--enter-duration:400ms] [--enter-y:20px] md:p-6"
            >
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase text-white">
                  Limited Edition
                </span>
              </div>
              <h2 className="text-2xl font-bold text-textPrimary">
                {product.title}
              </h2>
              {product.description && (
                <p className="text-sm text-textSecondary">
                  {product.description}
                </p>
              )}
              <ul className="flex flex-col gap-2">
                {features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm text-textPrimary"
                  >
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
              {remaining !== -100 && remaining > 0 && !product.owned && (
                <div>
                  <p className="text-sm font-bold text-primary">
                    Only {remaining.toLocaleString()} remaining at this price
                  </p>
                  <p className="text-xs text-textSecondary">{waveNote}</p>
                </div>
              )}
              <Button
                text={buttonText}
                onClick={purchaseHandler}
                loading={buttonLoading}
                disabled={product.owned || buttonLoading}
                classNames="w-full"
              />
              {!product.owned && (
                <p className="text-xs text-textSecondary">
                  {FOUNDERS_REFUND_POLICY}
                </p>
              )}
              {purchaseModalOpen && (
                <PurchaseModal
                  product={product}
                  closeHandler={() => setPurchaseModalOpen(false)}
                />
              )}
            </div>
          );
        })}
    </>
  );
};
